import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { User } from '../_interfaces/user.interface';
import { isPlatformBrowser } from '@angular/common';
import { Inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { ToasterManagerService } from './toastr-manager.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private isBrowser: boolean;
  router = inject(Router);
  toatr = inject(ToasterManagerService);

  constructor(
    private readonly http: HttpClient,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      const user = localStorage.getItem('currentUser');
      if (user) {
        this.currentUserSubject.next(JSON.parse(user));
      }
    }
  }

  login(credentials: { username: string; password: string }): Observable<User> {
    return this.http
      .post<User>(`${environment.api}/auth/login`, credentials, {
        withCredentials: true,
      })
      .pipe(
        tap((user) => {
          if (this.isBrowser) {
            localStorage.setItem('currentUser', JSON.stringify(user));
          }
          this.currentUserSubject.next(user);
        })
      );
  }

  logout(): Observable<any> {
    return this.http
      .get(`${environment.api}/auth/logout`, { withCredentials: true })
      .pipe(
        tap((res) => {
          if (this.isBrowser) {
            localStorage.removeItem('currentUser');
          }
          this.currentUserSubject.next(null);
          this.router.navigate(['/auth/login']);
        })
      );
  }

  register(payload: {
    email: string;
    firstName: string;
    lastName: string;
    isIncubator: boolean;
  }) {
    return this.http.post<void>(`${environment.api}/auth/register`, payload, {
      withCredentials: true,
    });
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  isLoggedIn(): boolean {
    return !!this.currentUserSubject.value;
  }

  forgotPassword(data: { email: string }) {
    return this.http.post(`${environment.api}/auth/forgot-password`, data);
  }

  resetPassword(data: { token: string; newPassword: string }) {
    return this.http.post(`${environment.api}/auth/reset-password`, data);
  }

  verifyEmail(token: string) {
    return this.http.get(`${environment.api}/auth/verify-email?token=${token}`);
  }
}
